import { h, mount } from "@/lib/dom";
import {
  loading,
  emptyState,
  button,
  toast,
  editModeButton,
  select as selectInput,
  openModal,
  field,
  textInput,
  swatchPicker,
  iconPicker,
} from "@/components/ui";
import { icon } from "@/lib/icons";
import { navigate } from "@/app/router";
import {
  subjectsService,
  timetableService,
  topicsService,
  holidaysService,
  homeworkService,
  findHoliday,
  pbErrorMessage,
  isSonstigesSubject,
  type TimetableSuggestion,
} from "@/services";
import type { Holiday } from "@/services";
import { voiceControl } from "@/features/voice/voicePanel";
import { openPeriodsModal } from "@/features/timetable/periodsModal";
import {
  WEEKDAY_LABEL,
  WEEKDAY_SHORT,
  addDays,
  breakAfter,
  buildBlocks,
  dateForWeekday,
  formatShortDate,
  isoWeekNumber,
  isSameDate,
  mondayOf,
  nowPosition,
  slotKey,
  toLocalISODate,
  toMinutes,
  weekdayOf,
} from "@/features/timetable/schedule";
import { markSlotSeen } from "@/features/timetable/lessonWatch";
import { openHomeworkList } from "@/features/homework/list";
import { openTopicForm } from "@/features/topics/topicForm";
import { chooseTopic } from "@/features/topics/topicPicker";
import { examTypeLabel } from "@/features/topics/examFields";
import { defaultColor, contrastText } from "@/lib/colors";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { checkConnectivity, connectivityMessage } from "@/services/offline/connectivity";
import { subjectIcon } from "@/lib/subjectIcons";
import type { ExamType, Homework, Period, Subject, TimetableSlot, Topic, Weekday } from "@/types/models";

type ViewMode = "week" | "day";

const VIEW_KEY = "lernapp.timetable.view";
/**
 * Unter dieser Breite (siehe Kopfzeile-Layout in v1.css, selbe Zahl) gibt es
 * nur noch die Tagesansicht: eine Wochenübersicht mit sieben schmalen Spalten
 * ist auf einem Handy ohnehin nicht mehr lesbar, und der Umschalter macht
 * damit Platz für eine einzeilige Kopfzeile.
 */
const FORCE_DAY_BELOW_PX = 767;

function isMobileWidth(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= FORCE_DAY_BELOW_PX;
}

function readView(): ViewMode {
  if (isMobileWidth()) return "day";
  return localStorage.getItem(VIEW_KEY) === "day" ? "day" : "week";
}

/** Eingabefeld fokussiert oder irgendein Modal offen – dann sollen Pfeiltasten nicht die Woche wechseln. */
function isTypingContext(el: Element | null): boolean {
  if (document.querySelector(".modal-backdrop")) return true;
  if (!el) return false;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
}

/** Ein Termin, der sich einer konkreten Stunde im Stundenplan zuordnen lässt. */
interface ExamMark {
  subjectId: string;
  topicId: string;
  date: string; // YYYY-MM-DD
  from: number;
  to: number;
  examType?: ExamType | "";
  topicTitle: string;
}

/** Stundenplan: Wochen- oder Tagesraster, im Bearbeitungsmodus per Drag & Drop befüllbar. */
export function TimetablePage(): HTMLElement {
  const container = h("div", null);

  let subjects: Subject[] = [];
  let slots: TimetableSlot[] = [];
  let periods: Period[] = [];
  let days: Weekday[] = [];
  let topics: Topic[] = [];
  let examMarks: ExamMark[] = [];
  let homework: Homework[] = [];
  let holidays: Holiday[] = [];
  const loadedHolidayYears = new Set<number>();
  let view: ViewMode = readView();
  /**
   * Am Wochenende (Sa/So) ist die laufende Unterrichtswoche schon vorbei –
   * Standard ist dann gleich die nächste Woche statt der gerade zu Ende
   * gegangenen.
   */
  function defaultWeekStart(): Date {
    const today = new Date();
    const monday = mondayOf(today);
    return weekdayOf(today) >= 5 ? addDays(monday, 7) : monday;
  }
  // Montag der angezeigten Woche (Wochenansicht) bzw. der Woche des
  // fokussierten Tages (Tagesansicht).
  let weekStart: Date = defaultWeekStart();
  let focusDate: Date = new Date();
  let initialized = false;
  let tickTimer: number | undefined;
  // Bearbeitungsmodus gilt nur für diesen Seitenbesuch – kein globaler,
  // persistenter Zustand. Beim Verlassen der Seite verschwindet die
  // Closure und der Modus ist automatisch wieder aus.
  let editing = false;
  // Getippter, noch nicht gespeicherter Raumtext je Block – überlebt einen
  // Re-Render, während der Nutzer noch tippt (z. B. wenn im Hintergrund die
  // Ferien nachgeladen werden und render() erneut läuft).
  const roomDraft: Record<string, string> = {};
  const blockKey = (weekday: Weekday, from: number) => `${weekday}:${from}`;

  /**
   * `mount()` ersetzt den kompletten Seiteninhalt – bei jedem Refresh (z. B.
   * nach Löschen einer Stunde) sonst ein spürbarer Sprung zurück an den
   * Seitenanfang. `.app-content` ist die eigentliche Scrollfläche (siehe
   * AppLayout.ts); ihre Position bleibt über den Neuaufbau hinweg erhalten.
   */
  function withScrollPreserved<T>(fn: () => T): T {
    const scroller = container.closest<HTMLElement>(".app-content");
    const top = scroller?.scrollTop;
    const result = fn();
    if (scroller && top != null) scroller.scrollTop = top;
    return result;
  }

  async function load(): Promise<void> {
    if (!initialized) mount(container, loading());
    try {
      const [subs, slotList, settings, topicList, hw] = await Promise.all([
        subjectsService.listReal(),
        timetableService.listSlots(),
        timetableService.getSettings(),
        topicsService.listAll(),
        homeworkService.listAll().catch(() => [] as Homework[]),
      ]);
      subjects = subs;
      slots = slotList;
      periods = settings.periods;
      days = settings.days;
      topics = topicList;
      examMarks = buildExamMarks(topicList);
      homework = hw;
      if (!days.includes(weekdayOf(focusDate))) {
        const first = days[0];
        if (first !== undefined) focusDate = dateForWeekday(weekStart, first);
      }
      initialized = true;
      withScrollPreserved(render);
    } catch (err) {
      initialized = true;
      const [tt, cachedSubjectsRaw, cachedTopics, cachedHomework] = await Promise.all([
        offlineSync.loadTimetable(),
        offlineSync.loadSubjects(),
        offlineSync.loadTopics(),
        offlineSync.loadHomeworkList(),
      ]);
      const cachedSubjects = cachedSubjectsRaw?.filter((s) => !isSonstigesSubject(s));
      if (tt && cachedSubjects) {
        subjects = cachedSubjects;
        slots = tt.slots;
        periods = tt.settings.periods;
        days = tt.settings.days;
        topics = cachedTopics ?? [];
        examMarks = buildExamMarks(cachedTopics ?? []);
        homework = cachedHomework ?? [];
        if (!days.includes(weekdayOf(focusDate))) {
          const first = days[0];
          if (first !== undefined) focusDate = dateForWeekday(weekStart, first);
        }
        withScrollPreserved(render);
        toast("Offline – zeige zwischengespeicherten Stand.", "warning");
        return;
      }
      const status = await checkConnectivity();
      mount(
        container,
        h(
          "div",
          { class: "card card-pad" },
          emptyState({
            icon: "calendar",
            title: "Stundenplan konnte nicht geladen werden",
            text: status.backendReachable ? pbErrorMessage(err) : connectivityMessage(status),
            action: button({ label: "Erneut versuchen", onClick: () => void load() }),
          })
        )
      );
    }
  }
  const refresh = () => void load();

  const subjectById = (id?: string) => (id ? subjects.find((s) => s.id === id) : undefined);

  /** Nur Termine mit Stundenplan-Bezug lassen sich einer Stunde zuordnen. */
  function buildExamMarks(topics: Topic[]): ExamMark[] {
    const marks: ExamMark[] = [];
    for (const topic of topics) {
      if (!topic.examDate || !topic.examPeriodFrom) continue;
      marks.push({
        subjectId: topic.subject,
        topicId: topic.id,
        date: topic.examDate.slice(0, 10),
        from: topic.examPeriodFrom,
        to: topic.examPeriodTo ?? topic.examPeriodFrom,
        examType: topic.examType,
        topicTitle: topic.title,
      });
    }
    return marks;
  }

  /** Hausaufgaben, die bis zu dieser Stunde fertig sein müssen. */
  function homeworkOf(subjectId: string, date: Date, from: number, to: number): Homework[] {
    const iso = toLocalISODate(date);
    return homework.filter(
      (hw) =>
        hw.subject === subjectId &&
        hw.dueDate === iso &&
        (!hw.duePeriod || (hw.duePeriod >= from && hw.duePeriod <= to))
    );
  }

  function examMarkFor(subjectId: string, date: Date, from: number, to: number): ExamMark | undefined {
    const iso = toLocalISODate(date);
    return examMarks.find(
      (m) => m.subjectId === subjectId && m.date === iso && m.from === from && m.to === to
    );
  }

  /**
   * Lädt Ferien/Feiertage für die Jahre der übergebenen Daten nach, falls
   * noch nicht geschehen (jahresweise gecached – siehe holidaysService).
   * Feuert im Hintergrund und rendert bei neuen Daten einmal neu; beim
   * erneuten Aufruf für ein schon geladenes Jahr passiert nichts.
   */
  function ensureHolidays(dates: Date[]): void {
    const years = [...new Set(dates.map((d) => d.getFullYear()))].filter(
      (y) => !loadedHolidayYears.has(y)
    );
    if (!years.length) return;
    years.forEach((y) => loadedHolidayYears.add(y));
    // holidaysService.forRange schlägt nie fehl (fängt API-Fehler selbst ab
    // und liefert dann leer) – der Stundenplan bleibt so immer nutzbar.
    void Promise.all(years.map((y) => holidaysService.forRange(new Date(y, 0, 1), new Date(y, 11, 31)))).then(
      (lists) => {
        holidays = [...holidays, ...lists.flat()];
        if (container.isConnected) render();
      }
    );
  }

  /* ---------- Kopfzeile ---------- */

  function header(): HTMLElement {
    // Auf dem Handy gibt es nur die Tagesansicht (siehe readView/isMobileWidth)
    // – der Umschalter hätte dort nichts zum Umschalten mehr.
    const viewSwitch = isMobileWidth() ? null : h(
      "div",
      { class: "view-switch", role: "tablist" },
      ...(["day", "week"] as ViewMode[]).map((mode) =>
        h(
          "button",
          {
            class: "view-opt" + (view === mode ? " active" : ""),
            role: "tab",
            "aria-selected": String(view === mode),
            onclick: () => {
              if (view === mode) return;
              // Beim Wechseln die jeweils andere Ansicht auf dieselbe Woche/denselben Tag ausrichten.
              if (mode === "day") focusDate = dateForWeekday(weekStart, weekdayOf(focusDate));
              else weekStart = mondayOf(focusDate);
              view = mode;
              localStorage.setItem(VIEW_KEY, mode);
              render();
            },
          },
          mode === "day" ? "Tag" : "Woche"
        )
      )
    );

    const isCurrent = view === "day" ? isSameDate(focusDate, new Date()) : isSameDate(weekStart, defaultWeekStart());

    // Drei Spalten (1fr auto 1fr): die Navigation sitzt dadurch exakt in der
    // Mitte der Kopfzeile und bleibt dort stehen, auch wenn rechts die
    // „Heute"-Pille dazukommt.
    return h(
      "div",
      { class: "page-header tt-header" },
      h("h1", null, "Stundenplan"),
      view === "day" ? dayNav() : weekNav(),
      h(
        "div",
        { class: "tt-header-right" },
        !isCurrent
          ? backToTodayBtn(
              view === "day" ? "Zu heute zurück" : "Zur aktuellen Woche zurück",
              view === "day" ? jumpToToday : jumpToTodayWeek
            )
          : null,
        viewSwitch,
        editing ? voiceTimetablePanel() : null,
        editModeButton({
          active: editing,
          onToggle: () => {
            editing = !editing;
            render();
          },
        }),
        button({
          icon: "settings",
          variant: "ghost",
          title: "Zeitraster bearbeiten",
          onClick: () => openPeriodsModal({ periods, days, onSaved: refresh }),
        })
      )
    );
  }

  /* ---------- Fächer-Palette (nur im Bearbeitungsmodus) ---------- */

  function palette(): HTMLElement | null {
    if (!editing) return null;
    if (!subjects.length) {
      return h(
        "div",
        { class: "card card-pad tt-palette-empty" },
        h("p", { class: "muted" }, "Lege zuerst Fächer an – sie erscheinen hier zum Hineinziehen.")
      );
    }

    const chip = (s: Subject) => {
      const accent = s.color || defaultColor();
      const el = h(
        "div",
        {
          class: "tt-chip",
          draggable: "true",
          style: `--accent:${accent};--on-accent:${contrastText(accent)}`,
          title: s.title,
        },
        h("span", { class: "row", html: subjectIcon(s.icon, 15) }),
        s.title
      );
      el.addEventListener("dragstart", (e) => {
        (e as DragEvent).dataTransfer?.setData("text/plain", s.id);
        if ((e as DragEvent).dataTransfer) (e as DragEvent).dataTransfer!.effectAllowed = "copy";
        el.classList.add("dragging");
      });
      el.addEventListener("dragend", () => el.classList.remove("dragging"));
      return el;
    };

    // Gleiche Gruppierung wie die Startseite: nach Kategorie, Reihenfolge =
    // position, Überschrift bei jedem Wechsel – am Bildschirmrand als Liste
    // statt als Zeile, die den Stundenplan nach unten verdrängt.
    const grouped: HTMLElement[] = [];
    let lastCat: string | null = null;
    for (const s of subjects) {
      const cat = s.category ?? "";
      if (cat && cat !== lastCat) grouped.push(h("h2", { class: "tt-palette-category" }, cat));
      if (cat) lastCat = cat;
      grouped.push(chip(s));
    }

    return h(
      "div",
      { class: "tt-palette" },
      h("span", { class: "tt-palette-label" }, "Fächer ziehen:"),
      h("div", { class: "tt-palette-items" }, ...grouped)
    );
  }

  /* ---------- Mikro-Erfassung ---------- */

  let voicePanelEl: HTMLElement | null = null;

  function buildVoiceContext(): unknown {
    return {
      subjects: subjects.map((s) => ({ id: s.id, title: s.title })),
      periodsCount: periods.length,
      days,
      slots: slots.map((s) => ({ weekday: s.weekday, period: s.period, subject: s.subject, room: s.room })),
    };
  }

  async function applyVoiceTimetable(p: TimetableSuggestion): Promise<void> {
    if (p.action === "remove") {
      const existing = slots.filter((s) => s.weekday === p.weekday && s.period === p.period);
      for (const s of existing) await timetableService.removeSlot(s.id);
      return;
    }
    if (!p.subject || !subjectById(p.subject)) throw new Error("Unbekanntes Fach – bitte Fach wählen.");
    await timetableService.setSlot(p.weekday as Weekday, p.period, p.subject, p.room);
  }

  function voiceTimetablePanel(): HTMLElement {
    voicePanelEl ??= voiceControl<TimetableSuggestion>({
      domain: "timetable",
      buildContext: buildVoiceContext,
      applyOne: applyVoiceTimetable,
      onApplied: refresh,
      columns: [
        {
          label: "Aktion",
          render: (p, patch) =>
            selectInput({
              value: p.action,
              options: [
                { value: "set", label: "Setzen/Ändern" },
                { value: "remove", label: "Löschen" },
              ],
              onchange: (e) => patch({ action: (e.target as HTMLSelectElement).value as TimetableSuggestion["action"] }),
            }),
        },
        {
          label: "Tag",
          render: (p, patch) =>
            selectInput({
              value: String(p.weekday),
              options: WEEKDAY_LABEL.map((label, i) => ({ value: String(i), label })),
              onchange: (e) => patch({ weekday: Number((e.target as HTMLSelectElement).value) }),
            }),
        },
        {
          label: "Stunde",
          render: (p, patch) =>
            selectInput({
              value: String(p.period),
              options: periods.map((_, i) => ({ value: String(i + 1), label: `${i + 1}. Stunde` })),
              onchange: (e) => patch({ period: Number((e.target as HTMLSelectElement).value) }),
            }),
        },
        {
          label: "Fach",
          render: (p, patch) =>
            selectInput({
              value: p.subject ?? "",
              options: subjects.map((s) => ({ value: s.id, label: s.title })),
              onchange: (e) => {
                const id = (e.target as HTMLSelectElement).value;
                patch({ subject: id, subjectTitle: subjectById(id)?.title });
              },
            }),
        },
        {
          label: "Raum",
          render: (p, patch) => {
            const el = h("input", { type: "text", class: "input", value: p.room ?? "", placeholder: "Raum" }) as HTMLInputElement;
            el.addEventListener("input", () => patch({ room: el.value }));
            return el;
          },
        },
      ],
    });
    return voicePanelEl;
  }

  /* ---------- Raster ---------- */

  /**
   * Baut die Zeilenhöhen des Rasters: je Stunde eine Zeile, dazwischen eine
   * Pausenzeile, deren Höhe der echten Pausenlänge entspricht. Blöcke über
   * mehrere Stunden überspannen die Pausenzeile und wachsen dadurch zusammen.
   */
  function gridRows(): string {
    const parts: string[] = [];
    periods.forEach((_, i) => {
      // Höhen als CSS-Variablen, damit kleine Schirme sie per Media-Query
      // verkleinern können, ohne dass hier gerechnet werden muss.
      parts.push("var(--tt-period-h)");
      if (i < periods.length - 1) {
        // Nahtlose Stunden bekommen keine Zeile, sonst der feste Trennstreifen.
        parts.push(breakAfter(periods, i) > 0 ? "var(--tt-break-h)" : "0px");
      }
    });
    return parts.join(" ");
  }

  /** Rasterzeile (1-basiert) für den Beginn einer Stunde. */
  const rowStart = (period: number) => period * 2 - 1;
  /** Endzeile für einen Block, der `span` Stunden umfasst. */
  const rowEnd = (period: number, span: number) => rowStart(period + span - 1) + 1;

  function timeColumn(): HTMLElement {
    const col = h("div", { class: "tt-times", style: `grid-template-rows:${gridRows()}` });
    periods.forEach((p, i) => {
      col.appendChild(
        h(
          "div",
          { class: "tt-time", style: `grid-row:${rowStart(i + 1)}` },
          h("span", { class: "t-start" }, p.start),
          h("span", { class: "t-end" }, p.end)
        )
      );
    });
    appendBreaks(col);
    return col;
  }

  /**
   * Pausenstriche einer Spalte. Sie liegen in derselben Spalte wie die
   * Stunden (nicht als Overlay über dem ganzen Raster) – dadurch stimmen die
   * Zeilen immer, unabhängig von der Spaltenzahl. Nur eine ruhige graue
   * Linie, keine Minutenangabe.
   */
  function appendBreaks(col: HTMLElement): void {
    periods.forEach((_, i) => {
      if (i >= periods.length - 1) return;
      const gap = breakAfter(periods, i);
      if (gap <= 0) return;
      col.appendChild(h("div", { class: "tt-break", style: `grid-row:${rowStart(i + 1) + 1}` }));
    });
  }

  function dayColumn(weekday: Weekday, date: Date): HTMLElement {
    const isToday = isSameDate(date, new Date());
    const holiday = findHoliday(holidays, toLocalISODate(date));
    const col = h("div", {
      class: "tt-day" + (isToday ? " is-today" : "") + (holiday ? " is-holiday" : ""),
      style: `grid-template-rows:${gridRows()}`,
    });

    // Hintergrund-Zellen: Ablagefläche im Bearbeitungsmodus, sonst nur Raster.
    periods.forEach((_, i) => {
      const period = i + 1;
      const cell = h("div", {
        class: "tt-cell",
        style: `grid-row:${rowStart(period)}`,
        dataset: { weekday: String(weekday), period: String(period) },
      });
      if (editing) {
        makeDropTarget(cell, weekday, period);
        cell.addEventListener("mousedown", (e) => startRangeDrag(e as MouseEvent, weekday, period));
      }
      col.appendChild(cell);
    });
    appendBreaks(col);

    for (const block of buildBlocks(slots, weekday, periods.length)) {
      const subject = subjectById(block.subject);
      // Ein Termin ohne Fach zeigt seine eigenen Farbe/Name/Icon-Felder statt
      // eines Fachs – nie ein Thema, also auch nie Prüfung/Hausaufgaben/Klick
      // in ein Thema (siehe `TimetableSlot.eventTitle` in models.ts).
      const blockTitle = subject?.title ?? block.eventTitle;
      const accent = subject?.color || block.eventColor || defaultColor();
      const to = block.from + block.span - 1;
      const mark = subject ? examMarkFor(subject.id, date, block.from, to) : undefined;
      const dueHomework = subject ? homeworkOf(subject.id, date, block.from, to) : [];
      const openHomework = dueHomework.filter((hw) => !hw.done).length;
      const baseTitle = blockTitle ? `${blockTitle} · ${periods[block.from - 1]?.start}–${periods[to - 1]?.end}` : "";
      // Nur echte Doppelstunden bekommen die eigene Zeile unter dem Fachnamen
      // (genug Höhe dafür) – eine Prüfung an einer EINZELnen Stunde bläht die
      // Kachel sonst unnötig auf und der Name läuft nicht mehr wie gewohnt
      // per Ellipsis ab, weil ihm plötzlich die ganze Zeile allein gehört.
      // Bei Einzelstunden bleibt der Raum rechts daneben, ergänzt ums Datum
      // (das steht sonst nirgends in der Kachel, nur im Tooltip).
      const wide = block.span >= 2;
      const roomText = editing ? (roomDraft[blockKey(weekday, block.from)] ?? block.room ?? "") : (block.room ?? "");
      const shortDate = mark && !wide ? formatShortDate(date) : "";
      const roomLabel = [block.room, shortDate].filter(Boolean).join(" · ");
      const roomNode =
        editing && blockTitle
          ? h("input", {
              class: "b-room b-room-input" + (wide ? " b-room-below" : ""),
              type: "text",
              placeholder: "Raum",
              value: roomText,
              onclick: (e: Event) => e.stopPropagation(),
              onmousedown: (e: Event) => e.stopPropagation(),
              oninput: (e: Event) => {
                roomDraft[blockKey(weekday, block.from)] = (e.target as HTMLInputElement).value;
              },
              onblur: (e: Event) => {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val !== (block.room ?? "")) void saveRoom(block.slots, blockKey(weekday, block.from), val);
                else delete roomDraft[blockKey(weekday, block.from)];
              },
              onkeydown: (e: KeyboardEvent) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              },
            })
          : roomLabel
            ? h("span", { class: "b-room" + (wide ? " b-room-below" : "") }, roomLabel)
            : null;
      const el = h(
        "div",
        {
          class: "tt-block" + (mark ? " is-exam" : ""),
          style:
            `grid-row:${rowStart(block.from)} / ${rowEnd(block.from, block.span)};` +
            `--accent:${accent};--on-accent:${contrastText(accent)}`,
          title: mark ? `${baseTitle} · ${examTypeLabel(mark.examType)}: ${mark.topicTitle}` : baseTitle,
        },
        h("span", { class: "b-icon", html: subjectIcon(subject?.icon ?? block.eventIcon, 15) }),
        h(
          "span",
          { class: "b-body" },
          h("span", { class: "b-title" }, blockTitle ?? "Unbekanntes Fach"),
          mark ? h("span", { class: "b-exam" }, examTypeLabel(mark.examType) || "Prüfung") : null,
          wide ? roomNode : null
        ),
        !wide ? roomNode : null,
        // Eigener Knopf: die Zahl öffnet die Hausaufgaben dieser Stunde,
        // statt wie der Rest der Kachel ins Thema zu springen.
        openHomework
          ? h(
              "button",
              {
                class: "b-hw",
                type: "button",
                title: `${openHomework} offene Hausaufgabe(n) bis hierher – anzeigen`,
                onclick: (e: MouseEvent) => {
                  e.stopPropagation();
                  const subjectId = subject!.id;
                  openHomeworkList({
                    title: `Hausaufgaben · ${subject!.title}`,
                    subjectTitle: subject!.title,
                    accent,
                    // Frisch holen: das Abhaken im Fenster soll auch die Zahl
                    // im Raster dahinter nachziehen.
                    load: async () => {
                      homework = await homeworkService.listAll().catch(() => homework);
                      const list = homeworkOf(subjectId, date, block.from, to);
                      render();
                      return list;
                    },
                  });
                },
              },
              String(openHomework)
            )
          : null
      );

      if (editing) {
        // Auch belegte Felder nehmen ein anderes Fach entgegen (überschreiben).
        makeDropTarget(el, weekday, block.from);
      } else if (subject) {
        el.classList.add("is-clickable");
        el.addEventListener("click", () => openSubject(subject, weekday, block.from, date, mark));
      }

      col.appendChild(el);
    }

    return col;
  }

  /**
   * Zeitlinie: liegt absolut über der heutigen Spalte, damit sie auch in
   * Pausen an der richtigen Stelle steht.
   *
   * Wichtig: Diese Funktion darf erst laufen, wenn die Spalte tatsächlich im
   * Dokument hängt – `offsetTop`/`offsetHeight` liefern für noch nicht
   * eingehängte Elemente 0. Deshalb wird sie nicht beim Bauen der Spalte
   * (dayColumn, noch im Speicher) aufgerufen, sondern von `updateNowLine()`
   * über das schon gemountete DOM. Direkt nach dem Laden stand die Linie
   * sonst ganz oben, bis der erste Intervall-Tick sie eine Minute später
   * korrigierte.
   */
  function nowTop(dayCol: HTMLElement): number | null {
    if (!periods.length) return null;
    const first = toMinutes(periods[0].start);
    const last = toMinutes(periods[periods.length - 1].end);
    if (first === null || last === null) return null;
    const pos = nowPosition(periods);
    if (pos.minutes < first || pos.minutes > last) return null;

    const cells = Array.from(dayCol.querySelectorAll<HTMLElement>(":scope > .tt-cell"));

    if (pos.period !== null) {
      const cell = cells[pos.period - 1];
      return cell ? cell.offsetTop + cell.offsetHeight * pos.progress : null;
    }
    // In einer Pause: an der zuletzt beendeten Stunde andocken.
    for (let i = periods.length - 1; i >= 0; i--) {
      const e = toMinutes(periods[i].end);
      if (e !== null && pos.minutes >= e) {
        const cell = cells[i];
        return cell ? cell.offsetTop + cell.offsetHeight : null;
      }
    }
    return null;
  }

  /**
   * Aktualisiert die Zeitlinie im schon gemounteten DOM. Bewegt ein
   * bestehendes Element per CSS-Transition sanft an die neue Position
   * („lebendig") statt es bei jedem Tick neu zu erzeugen und springen zu
   * lassen.
   */
  function updateNowLine(): void {
    const todayCol = container.querySelector<HTMLElement>(".tt-day.is-today");
    if (!todayCol) return;
    const top = nowTop(todayCol);
    let line = todayCol.querySelector<HTMLElement>(".tt-now");
    if (top === null) {
      line?.remove();
      return;
    }
    if (!line) {
      // Neu erzeugt bereits an der richtigen Stelle – keine Transition vom
      // Seitenrand herein.
      line = h("div", { class: "tt-now", style: `top:${top}px` });
      todayCol.appendChild(line);
      return;
    }
    line.style.top = `${top}px`;
  }

  function makeDropTarget(el: HTMLElement, weekday: Weekday, period: number): void {
    el.addEventListener("dragover", (e) => {
      e.preventDefault();
      (e as DragEvent).dataTransfer!.dropEffect = "copy";
      el.classList.add("drop-hover");
    });
    el.addEventListener("dragleave", () => el.classList.remove("drop-hover"));
    el.addEventListener("drop", (e) => {
      e.preventDefault();
      el.classList.remove("drop-hover");
      const subjectId = (e as DragEvent).dataTransfer?.getData("text/plain");
      if (subjectId) void assign(weekday, period, subjectId);
    });
    // Ohne Maus: Tippen auf ein leeres Feld bietet die Fächerauswahl an.
    el.addEventListener("click", () => pickSubject(weekday, period));
  }

  /**
   * Über leere Felder ziehen, um mehrere Stunden auf einmal auszuwählen
   * (z. B. eine Doppelstunde in einem Zug anlegen statt Feld für Feld). Die
   * Auswahl stoppt an der ersten schon belegten Stunde – ein einfacher Klick
   * ohne Ziehen bleibt unangetastet und läuft weiter über den normalen
   * `click`-Handler aus `makeDropTarget`.
   */
  function startRangeDrag(e: MouseEvent, weekday: Weekday, startPeriod: number): void {
    if (e.button !== 0) return;
    let dragged = false;
    let to = startPeriod;

    const cellsInCol = () =>
      Array.from(container.querySelectorAll<HTMLElement>(`.tt-cell[data-weekday="${weekday}"]`));

    const paint = () => {
      const lo = Math.min(startPeriod, to);
      const hi = Math.max(startPeriod, to);
      for (const c of cellsInCol()) {
        const p = Number(c.dataset.period);
        c.classList.toggle("drag-select", p >= lo && p <= hi);
      }
    };

    const onMove = (ev: MouseEvent) => {
      const hovered = (document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null)?.closest<HTMLElement>(
        ".tt-cell"
      );
      if (!hovered || hovered.dataset.weekday !== String(weekday)) return;
      const target = Number(hovered.dataset.period);
      if (!target || target === to) return;

      // Nur durch leere, zusammenhängende Felder erweitern – an der ersten
      // belegten Stunde ist Schluss.
      const dir = target >= startPeriod ? 1 : -1;
      let reach = startPeriod;
      for (let p = startPeriod + dir; dir > 0 ? p <= target : p >= target; p += dir) {
        if (slots.some((s) => s.weekday === weekday && s.period === p)) break;
        reach = p;
      }
      if (reach !== to) {
        dragged = true;
        to = reach;
        paint();
      }
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      for (const c of cellsInCol()) c.classList.remove("drag-select");
      if (dragged && to !== startPeriod) {
        pickSubject(weekday, Math.min(startPeriod, to), Math.max(startPeriod, to));
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  /* ---------- Aktionen ---------- */

  async function assign(weekday: Weekday, period: number, subjectId: string): Promise<void> {
    try {
      await timetableService.setSlot(weekday, period, subjectId);
      await load();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Mehrere Stunden auf einmal belegen (z. B. per Ziehen aufgezogene Doppelstunde) – ein Neuladen für alle zusammen. */
  async function assignRange(weekday: Weekday, periods_: number[], subjectId: string): Promise<void> {
    try {
      await Promise.all(periods_.map((p) => timetableService.setSlot(weekday, p, subjectId)));
      await load();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Wie `assignRange`, nur für einen Termin ohne Fach (siehe `openEventEditor`). */
  async function assignEventRange(
    weekday: Weekday,
    periods_: number[],
    event: { title: string; color: string; icon?: string }
  ): Promise<void> {
    try {
      await Promise.all(periods_.map((p) => timetableService.setEventSlot(weekday, p, event)));
      await load();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Raum für alle Stunden eines Blocks setzen (Doppelstunde = mehrere Slots). */
  async function saveRoom(blockSlots: TimetableSlot[], key: string, room: string): Promise<void> {
    try {
      await Promise.all(blockSlots.map((s) => timetableService.setRoom(s.id, room)));
      delete roomDraft[key];
      await load();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  async function removeBlock(blockSlots: TimetableSlot[]): Promise<void> {
    try {
      await Promise.all(blockSlots.map((s) => timetableService.removeSlot(s.id)));
      await load();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Auswahl per Tippen – auf dem Handy ist Drag & Drop unpraktisch. */
  /**
   * Fach für eine Stunde wählen – oder für einen per Ziehen aufgezogenen
   * Bereich leerer Stunden gleich für alle zusammen (z. B. eine Doppelstunde
   * in einem Zug anlegen statt Feld für Feld).
   */
  function pickSubject(weekday: Weekday, periodFrom: number, periodTo: number = periodFrom): void {
    const range = Array.from({ length: periodTo - periodFrom + 1 }, (_, i) => periodFrom + i);
    const existing = slots.filter((s) => s.weekday === weekday && range.includes(s.period));
    const single = range.length === 1;
    // Ein bestehender Termin OHNE Fach hat keine Liste zum Umschalten nötig –
    // Klick geht direkt in seinen Editor (Farbe/Name/Icon + Löschen).
    if (single && existing[0] && !existing[0].subject) {
      openEventEditor(existing[0]);
      return;
    }
    const list = h(
      "div",
      { class: "tt-pick-list" },
      ...subjects.map((s) =>
        h(
          "button",
          {
            class: "tt-pick" + (single && existing[0]?.subject === s.id ? " active" : ""),
            style: `--accent:${s.color || defaultColor()}`,
            onclick: () => {
              handle.close();
              void assignRange(weekday, range, s.id);
            },
          },
          h("span", { class: "row", html: subjectIcon(s.icon, 16) }),
          s.title
        )
      ),
      h(
        "button",
        {
          class: "tt-pick tt-pick-sonstiges",
          title: "Termin nur für den Stundenplan anlegen (z. B. freiwilliger Chor) – kein Fach, keine Materialien/Mitschrift/Hausaufgaben",
          onclick: () => {
            handle.close();
            openEventEditor(undefined, weekday, range);
          },
        },
        h("span", { class: "row", html: icon("plus", 15) }),
        "Sonstiges"
      ),
      existing.length
        ? h(
            "button",
            {
              class: "tt-pick tt-pick-clear",
              onclick: () => {
                handle.close();
                void removeBlock(existing);
              },
            },
            h("span", { class: "row", html: icon("trash", 15) }),
            "Feld leeren"
          )
        : null
    );
    const handle = openPickModal(
      `${WEEKDAY_LABEL[weekday]}, ${single ? `${periodFrom}. Stunde` : `${periodFrom}.–${periodTo}. Stunde`}`,
      `${periods[periodFrom - 1]?.start}–${periods[periodTo - 1]?.end}`,
      list
    );
  }

  /**
   * Editor für einen Termin OHNE Fach (Farbe/Name/Icon, siehe `subjectForm.ts`
   * – bewusst kein Fach: kein Thema, keine Materialien/Mitschrift/Hausaufgaben,
   * der Termin lebt einzig als Feld im Stundenplan). Zwei Aufrufarten:
   *   - `existing` gesetzt: bestehenden Termin bearbeiten/löschen (ein Feld).
   *   - `weekday`/`range` gesetzt: neuen Termin für den (ggf. per Ziehen
   *     aufgezogenen) Bereich anlegen.
   */
  function openEventEditor(existing?: TimetableSlot, weekday?: Weekday, range?: number[]): void {
    const titleInput = textInput({
      value: existing?.eventTitle ?? "",
      placeholder: "z. B. Chor",
      required: true,
    });
    const colorPicker = swatchPicker(existing?.eventColor || defaultColor());
    const icons = iconPicker(existing?.eventIcon || "star");
    icons.setQuery(titleInput.value);
    titleInput.addEventListener("input", () => icons.setQuery(titleInput.value));
    const errorBox = h("div", { class: "field-error" });
    const saveBtn = button({ label: existing ? "Speichern" : "Anlegen", variant: "primary", onClick: () => void save() });

    async function save(): Promise<void> {
      const title = titleInput.value.trim();
      if (!title) {
        errorBox.textContent = "Bitte einen Namen eingeben.";
        return;
      }
      saveBtn.disabled = true;
      errorBox.textContent = "";
      const event = { title, color: colorPicker.getValue(), icon: icons.getValue() };
      try {
        if (existing) {
          await timetableService.setEventSlot(existing.weekday, existing.period, event, existing.room);
          await load();
        } else if (weekday !== undefined && range) {
          await assignEventRange(weekday, range, event);
        }
        toast(existing ? "Termin aktualisiert." : "Termin angelegt.", "success");
        handle.close();
      } catch (err) {
        errorBox.textContent = pbErrorMessage(err);
        saveBtn.disabled = false;
      }
    }

    async function remove(): Promise<void> {
      if (!existing) return;
      handle.close();
      await removeBlock([existing]);
    }

    titleInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void save();
      }
    });

    const handle = openModal({
      title: existing ? "Termin bearbeiten" : "Termin anlegen",
      body: h(
        "div",
        null,
        field("Name", titleInput),
        field("Farbe", colorPicker.el),
        field("Icon", icons.el),
        errorBox
      ),
      footer: h(
        "div",
        { class: "row" },
        existing
          ? button({ icon: "trash", label: "Löschen", variant: "ghost", onClick: () => void remove() })
          : null,
        h("span", { class: "spacer" }),
        button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
        saveBtn
      ),
    });

    setTimeout(() => titleInput.focus(), 50);
  }

  /**
   * Springt ins Thema des Fachs. Steht an dieser Stunde ein Prüfungstermin,
   * geht es immer zu dessen Thema – unabhängig von Status/Archiv, das war
   * ja genau die Stunde, in der es lief. Sonst entscheidet der gemeinsame
   * Themen-Wähler (features/topics/topicPicker.ts): laufen mehrere Themen
   * nebeneinander, fragt er nach, sonst geht es direkt hinein. Gibt es noch
   * gar kein Thema, öffnet sich der Anlege-Dialog.
   *
   * `markSlotSeen` nur auslösen, wenn wirklich die heutige Spalte angeklickt
   * wurde – sonst würde das Browsen zu einer anderen Woche denselben
   * Wochentag/Stunde-Schlüssel für heute fälschlich als „schon gesehen"
   * markieren und der Stundenwächter (lessonWatch) für die echte laufende
   * Stunde später fälschlich still bleiben.
   */
  async function openSubject(
    subject: Subject,
    weekday: Weekday,
    period: number,
    date: Date,
    mark?: ExamMark
  ): Promise<void> {
    if (isSameDate(date, new Date())) markSlotSeen(slotKey(weekday, period));
    try {
      if (mark) {
        const examTopic = await topicsService.get(mark.topicId).catch(() => undefined);
        if (examTopic) {
          navigate(`/topic/${examTopic.id}`);
          return;
        }
        // Thema inzwischen gelöscht: normale Auflösung als Rückfall.
      }

      const own = topics.filter((t) => t.subject === subject.id);
      if (!own.some((t) => t.state !== "archived")) {
        openTopicForm({ subjectId: subject.id, onSaved: refresh });
        return;
      }
      const target = await chooseTopic({ subject, topics: own });
      if (target) navigate(`/topic/${target.id}`);
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /* ---------- Navigation (Woche/Tag, echte Kalenderdaten) ---------- */

  function stepWeek(delta: number): void {
    weekStart = addDays(weekStart, delta * 7);
    render();
  }

  function jumpToTodayWeek(): void {
    weekStart = defaultWeekStart();
    render();
  }

  /** Nächster/vorheriger angezeigter Wochentag, auch über Wochengrenzen hinweg. */
  function stepDay(delta: number): void {
    let d = focusDate;
    for (let i = 0; i < 14; i++) {
      d = addDays(d, delta);
      if (days.includes(weekdayOf(d))) {
        focusDate = d;
        weekStart = mondayOf(d);
        render();
        return;
      }
    }
  }

  function jumpToToday(): void {
    focusDate = new Date();
    weekStart = mondayOf(focusDate);
    render();
  }

  /** Kleiner, auffälliger Pill-Knopf – erscheint nur, wenn man nicht mehr auf heute/der aktuellen Woche steht. */
  function backToTodayBtn(title: string, onClick: () => void): HTMLElement {
    return h(
      "button",
      { class: "tt-today-btn", type: "button", title, onclick: onClick },
      h("span", { class: "row", html: icon("undo", 13) }),
      "Heute"
    );
  }

  function weekNav(): HTMLElement {
    const isCurrentWeek = isSameDate(weekStart, mondayOf(new Date()));
    return h(
      "div",
      { class: "tt-weeknav" },
      button({ icon: "chevron-left", variant: "ghost", size: "sm", title: "Vorherige Woche", onClick: () => stepWeek(-1) }),
      h(
        "div",
        { class: "tt-weeknav-title" + (isCurrentWeek ? " is-current" : "") },
        `Kalenderwoche ${isoWeekNumber(weekStart)}`
      ),
      button({ icon: "chevron-right", variant: "ghost", size: "sm", title: "Nächste Woche", onClick: () => stepWeek(1) })
    );
  }

  function dayNav(): HTMLElement {
    const isToday = isSameDate(focusDate, new Date());
    return h(
      "div",
      { class: "tt-daynav" },
      button({ icon: "chevron-left", variant: "ghost", size: "sm", title: "Vorheriger Tag", onClick: () => stepDay(-1) }),
      h(
        "button",
        {
          type: "button",
          class: "tt-daynav-title" + (isToday ? " is-current" : ""),
          title: isToday ? undefined : "Zu heute zurück",
          onclick: () => (isToday ? null : jumpToToday()),
        },
        `${WEEKDAY_LABEL[weekdayOf(focusDate)]}, ${formatShortDate(focusDate)}`
      ),
      button({ icon: "chevron-right", variant: "ghost", size: "sm", title: "Nächster Tag", onClick: () => stepDay(1) })
    );
  }

  /* ---------- Rendering ---------- */

  function grid(): HTMLElement {
    const shownDays: Weekday[] = view === "day" ? [weekdayOf(focusDate)] : days;
    const shownDates: Date[] = view === "day" ? [focusDate] : days.map((d) => dateForWeekday(weekStart, d));
    ensureHolidays(shownDates);

    const head = h(
      "div",
      { class: "tt-head", style: `grid-template-columns:var(--tt-time-col) repeat(${shownDays.length}, minmax(0, 1fr))` },
      h("div", { class: "tt-head-corner" }),
      ...shownDays.map((d, i) => {
        const date = shownDates[i];
        const holiday = findHoliday(holidays, toLocalISODate(date));
        return h(
          "div",
          {
            class: "tt-head-day" + (isSameDate(date, new Date()) ? " is-today" : "") + (holiday ? " is-holiday" : ""),
            title: holiday?.name,
          },
          h("span", { class: "d-short" }, `${WEEKDAY_SHORT[d]} ${formatShortDate(date)}`),
          h("span", { class: "d-long" }, `${WEEKDAY_LABEL[d]}, ${formatShortDate(date)}`),
          // Immer vorhanden, auch ohne Ferien – sonst wären die Köpfe
          // unterschiedlich hoch und das Raster würde beim Nachladen der
          // Ferien springen.
          h("span", { class: "d-holiday" }, holiday?.name ?? "")
        );
      })
    );

    const body = h(
      "div",
      { class: "tt-body", style: `grid-template-columns:var(--tt-time-col) repeat(${shownDays.length}, minmax(0, 1fr))` },
      timeColumn(),
      ...shownDays.map((d, i) => dayColumn(d, shownDates[i]))
    );

    return h("div", { class: "tt-grid card" + (view === "day" ? " is-day" : "") }, head, h("div", { class: "tt-scroll" }, body));
  }

  function render(): void {
    if (!periods.length) {
      mount(
        container,
        h(
          "div",
          null,
          header(),
          h(
            "div",
            { class: "card card-pad" },
            emptyState({
              icon: "clock",
              title: "Noch kein Zeitraster",
              text: "Lege fest, wann deine Stunden beginnen und enden.",
              action: button({
                label: "Zeitraster einrichten",
                variant: "primary",
                onClick: () =>
                  openPeriodsModal({ periods, days, onSaved: refresh }),
              }),
            })
          )
        )
      );
      return;
    }

    mount(
      container,
      h(
        "div",
        { class: "tt-page" + (editing ? " is-editing" : "") },
        header(),
        h("div", { class: "tt-edit-layout" }, palette(), grid())
      )
    );
    // Erst jetzt ist die Spalte im Dokument und hat echte Maße.
    updateNowLine();
    scheduleTick();
  }

  /**
   * Hält die Zeitlinie aktuell, ohne die ganze Seite neu zu bauen: alle paar
   * Sekunden wird nur ihre Position gesetzt. Die CSS-Transition auf `.tt-now`
   * gleitet dabei sanft zur neuen Stelle, statt zu springen – dadurch wirkt
   * die Linie live statt einmal pro Minute umzuspringen.
   */
  function scheduleTick(): void {
    window.clearInterval(tickTimer);
    tickTimer = window.setInterval(() => {
      if (!container.isConnected) {
        window.clearInterval(tickTimer);
        return;
      }
      updateNowLine();
    }, 15_000);
  }

  // Pfeiltasten blättern zwischen Wochen (Wochenansicht) bzw. Tagen
  // (Tagesansicht) – nicht, während irgendwo getippt wird oder ein Modal
  // offen ist. Der Listener endet mit der Seite.
  const onKeydown = (e: KeyboardEvent) => {
    if (!container.isConnected) {
      window.removeEventListener("keydown", onKeydown);
      return;
    }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (isTypingContext(document.activeElement)) return;
    if (!initialized || !periods.length) return;
    const delta = e.key === "ArrowLeft" ? -1 : 1;
    if (view === "day") stepDay(delta);
    else stepWeek(delta);
  };
  window.addEventListener("keydown", onKeydown);

  void load();
  return container;
}

/** Schlanker Auswahl-Dialog fürs Belegen eines Feldes per Tippen. */
function openPickModal(title: string, subtitle: string, body: HTMLElement) {
  const backdrop = h("div", { class: "modal-backdrop" });
  const close = () => backdrop.remove();
  const modal = h(
    "div",
    { class: "modal" },
    h(
      "div",
      { class: "modal-header" },
      h("div", { class: "modal-head-text" }, h("h2", null, title), h("div", { class: "page-subtitle" }, subtitle)),
      button({ icon: "x", variant: "ghost", size: "sm", title: "Schließen", onClick: close })
    ),
    h("div", { class: "modal-body" }, body)
  );
  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) close();
  });
  document.body.appendChild(backdrop);
  return { close };
}
